import { createServerFn } from "@tanstack/react-start";
import { createClient } from "./supabase/admin";
import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { Status } from "./supabase/enums";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { getAddress } from "./postcode";
import Decimal from "decimal.js";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";

const timezone = "Europe/Amsterdam";

// The customer app only ever orders for "today", so a per-day delivery-area
// blackout only needs to be checked against the current business-timezone date.
const getBusinessToday = () =>
  format(toZonedTime(new Date(), timezone), "yyyy-MM-dd");

// export const deliveryAreaMutationOptions = () =>
//   mutationOptions({
//     mutationKey: ["delivery-area"],
//     mutationFn: checkDeliveryArea,
//   });

export const deliveryAreaQueryOptions = (
  zipCode: string,
  streetNumber: string,
  sessionId: string
) =>
  queryOptions({
    queryKey: ["delivery-area", zipCode, streetNumber, sessionId],
    queryFn: () =>
      checkDeliveryArea({
        data: {
          zipCode,
          streetNumber,
          sessionId,
        },
      }),
  });

export const checkDeliveryArea = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(
    z.object({
      zipCode: z.string(),
      streetNumber: z.string(),
      sessionId: z.string(),
    })
  )
  .handler(async ({ context: { userId }, data }) => {
    const supabase = createClient();
    const zipCodeWithoutLetters = data.zipCode.replace(/[a-zA-Z]/g, "").trim();

    const deliveryAreaPromise = supabase
      .from("delivery_area")
      .select(
        "id, minOrderAmount:min_order_amount, fee, freeDeliveryFrom:free_delivery_from, zipCodes:zip_code_to_delivery_area!inner(zipCode:zip_code, status)"
      )
      .eq("user_id", userId)
      .eq("status", Status.active)
      .in("zip_code_to_delivery_area.zip_code", [zipCodeWithoutLetters])
      .eq("zip_code_to_delivery_area.status", Status.active)
      .limit(1)
      .maybeSingle();

    const [deliveryAreaResult] = await Promise.all([deliveryAreaPromise]);

    if (deliveryAreaResult.error) {
      console.error(deliveryAreaResult.error);
      return {
        success: false,
        code: "general_error",
      };
    }


    const deliveryArea = deliveryAreaResult.data;

    if (!deliveryArea) {
      return {
        success: false,
        code: "address_not_in_delivery_area",
      };
    }

    // Per-day blackout: staff can pause delivery to this area for the current
    // day from the POS. Treat a blacked-out area as unavailable today.
    const { data: blackout, error: blackoutError } = await supabase
      .from("delivery_area_blackout_date")
      .select("id")
      .eq("user_id", userId)
      .eq("delivery_area_id", deliveryArea.id)
      .eq("date", getBusinessToday())
      .limit(1)
      .maybeSingle();

    if (blackoutError) {
      console.error(blackoutError);
      return {
        success: false,
        code: "general_error",
      };
    }

    if (blackout) {
      return {
        success: false,
        code: "delivery_area_unavailable_today",
      };
    }

    // check if address is valid
    try {
      const address = await getAddress({
        data: {
          streetNumber: data.streetNumber,
          postalCode: data.zipCode,
          sessionId: data.sessionId,
        },
      });

      return {
        success: true,
        deliveryArea: {
          id: deliveryArea.id,
          minOrderAmount: deliveryArea.minOrderAmount,
          freeDeliveryFrom: deliveryArea.freeDeliveryFrom
            ? new Decimal(deliveryArea.freeDeliveryFrom).div(100).toNumber()
            : undefined,
          fee: new Decimal(deliveryArea.fee).div(100).toNumber(),
          zipCodes: deliveryArea.zipCodes,
        },
        address,
      };
    } catch (error) {
      console.error(error);
      return {
        success: false,
        code: "address_not_valid",
      };
    }
  });

export type DeliveryArea = Awaited<
  ReturnType<typeof checkDeliveryArea>
>["deliveryArea"];
